import React, { useState, useEffect, useCallback } from 'react';
import { PageHeader } from './PageHeader';
import { apiClient, handleApiError } from '../services/api';
import { useTranslation } from '../i18n';
import './SettingsPage.css';

interface PushSetting {
  name: string;
  description: string | null;
  enabled: boolean;
}

interface PushSettings {
  [key: string]: PushSetting;
}

export const NotificationsPage: React.FC = () => {
  const [settings, setSettings] = useState<PushSettings>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const t = useTranslation();

  // Fetch settings on mount
  useEffect(() => {
    const fetchSettings = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await apiClient.getPushSettings();
        setSettings(response);
      } catch (err: any) {
        setError(handleApiError(err));
      } finally {
        setLoading(false);
      }
    };

    fetchSettings();
  }, []);

  // Toggle a single setting
  const toggleSetting = useCallback(async (type: string) => {
    if (saving) return; // Prevent multiple simultaneous saves

    const currentValue = settings[type]?.enabled ?? true;
    const newValue = !currentValue;

    // Optimistically update UI
    setSettings(prev => ({
      ...prev,
      [type]: {
        ...prev[type],
        enabled: newValue,
      },
    }));

    setSaving(type);
    setError(null);

    try {
      await apiClient.updatePushSettings({
        [type]: newValue,
      });
    } catch (err: any) {
      // Revert on error
      setSettings(prev => ({
        ...prev,
        [type]: {
          ...prev[type],
          enabled: currentValue,
        },
      }));
      setError(handleApiError(err));
    } finally {
      setSaving(null);
    }
  }, [settings, saving]);

  // Group settings for display
  const reminderTypes = ['daily_reminder', 'task_reminder', 'journal_reminder'];
  const updateTypes = ['progress_updates', 'promo'];

  const renderSettingItem = (type: string) => {
    const setting = settings[type];
    if (!setting) return null;

    return (
      <div className="settings-item" key={type}>
        <div className="settings-item-info">
          <div className="settings-item-label">{setting.name}</div>
          {setting.description && (
            <div className="settings-item-description">{setting.description}</div>
          )}
        </div>
        <label className="toggle-switch">
          <input
            type="checkbox"
            checked={setting.enabled}
            onChange={() => toggleSetting(type)}
            disabled={saving === type}
          />
          <span className={`toggle-slider ${saving === type ? 'loading' : ''}`}></span>
        </label>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="page-container settings-page">
        <PageHeader title={t.notifications.title} />
        <div className="settings-content">
          <div className="loading-state">
            <div className="loading-spinner"></div>
            <p>{t.notifications.loadingSettings}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="page-container settings-page">
      <PageHeader title={t.notifications.title} />

      <div className="settings-content">
        {error && (
          <div className="error-message">
            {error}
          </div>
        )}

        <div className="settings-group">
          <h2 className="settings-group-title">{t.notifications.reminders}</h2>
          {reminderTypes.map(type => renderSettingItem(type))}
        </div>

        <div className="settings-group">
          <h2 className="settings-group-title">{t.notifications.updates}</h2>
          {updateTypes.map(type => renderSettingItem(type))}
        </div>

        <div className="settings-hint">
          <p>{t.notifications.settingsHint}</p>
        </div>
      </div>
    </div>
  );
};
